import { SEOMetadata } from '@/types/seo';
import { getBrand, BrandConfig } from '@/config/brands';

// JSON-LD Schema Types
export interface Organization {
  '@type': 'Organization' | 'Hospital';
  name: string;
  url: string;
  logo?: string;
  description?: string;
  contactPoint?: ContactPoint[];
  address?: PostalAddress;
  foundingDate?: string;
}

export interface ContactPoint {
  '@type': 'ContactPoint';
  telephone?: string;
  contactType: string;
  email?: string;
}

export interface PostalAddress {
  '@type': 'PostalAddress';
  streetAddress?: string;
  addressLocality: string;
  addressRegion: string;
  postalCode?: string;
  addressCountry: string;
}

export interface Hospital extends Organization {
  '@type': 'Hospital';
  medicalSpecialty?: string[];
  availableService?: MedicalService[];
  paymentAccepted?: string[];
  priceRange?: string;
}

export interface MedicalService {
  '@type': 'MedicalService';
  name: string;
  serviceType: string;
  description?: string;
  provider: Organization;
  offers?: Offer[];
}

export interface MedicalProcedure {
  '@type': 'MedicalProcedure' | 'SurgicalProcedure' | 'DiagnosticProcedure';
  name: string;
  alternateName?: string[];
  code?: MedicalCode[];
  description?: string;
  bodyLocation?: string;
  preparation?: string;
  followup?: string;
  howPerformed?: string;
  procedureType?: string;
  priceRange?: string;
  typicalAgeRange?: string;
  price?: PriceSpecification;
  offers?: Offer[];
  medicalSpecialty?: string[];
}

export interface MedicalCode {
  '@type': 'MedicalCode';
  code: string;
  codingSystem: 'CPT' | 'DRG' | 'ICD-10' | 'HCPCS' | 'Revenue' | 'NDC';
  codeValue?: string;
  description?: string;
  version?: string;
  url?: string;
}

export interface PriceSpecification {
  '@type': 'PriceSpecification' | 'UnitPriceSpecification';
  price: number | string;
  priceCurrency: string;
  valueAddedTaxIncluded?: boolean;
  validFrom?: string;
  validThrough?: string;
  billingIncrement?: number;
  unitCode?: string;
  unitText?: string;
  priceType?: 'ListPrice' | 'MSRP' | 'MinimumAdvertisedPrice' | 'SalePrice';
  priceComponentType?: 'ActivationFee' | 'CleanupFee' | 'DistanceFee' | 'Downpayment' | 'Installment' | 'Subscription';
}

export interface Offer {
  '@type': 'Offer';
  price?: string;
  priceCurrency: string;
  priceValidUntil?: string;
  availability?: string;
  seller?: Organization;
  description?: string;
  priceSpecification?: PriceSpecification;
  eligibleQuantity?: QuantitativeValue;
  itemCondition?: 'NewCondition' | 'UsedCondition' | 'RefurbishedCondition';
}

export interface QuantitativeValue {
  '@type': 'QuantitativeValue';
  value?: number;
  minValue?: number;
  maxValue?: number;
  unitCode?: string;
  unitText?: string;
}

export interface Dataset {
  '@type': 'Dataset';
  name: string;
  description: string;
  url: string;
  creator: Organization;
  datePublished?: string;
  dateModified?: string;
  keywords?: string[];
  license?: string;
  distribution?: DataDownload[];
}

export interface DataDownload {
  '@type': 'DataDownload';
  encodingFormat: string;
  contentUrl: string;
  contentSize?: string;
}

export interface WebSite {
  '@type': 'WebSite';
  name: string;
  description: string;
  url: string;
  potentialAction?: SearchAction;
}

export interface SearchAction {
  '@type': 'SearchAction';
  target: string;
  'query-input': string;
}

export interface HealthTopicContent {
  '@type': 'HealthTopicContent';
  name: string;
  description: string;
  hasHealthAspect?: HealthAspectEnumeration[];
  about?: MedicalCondition[];
  mentions?: MedicalEntity[];
  datePublished?: string;
  dateModified?: string;
  author?: Organization | Person;
}

export interface HealthAspectEnumeration {
  '@type': 'HealthAspectEnumeration';
  name: 'Benefits' | 'Causes' | 'Contagiousness' | 'EffectivenessHealthAspect' | 'GettingAccessHealthAspect' | 'HowOrWhereHealthAspect' | 'LivingWithHealthAspect' | 'MayTreatHealthAspect' | 'MisconceptionsHealthAspect' | 'Overview' | 'PatientExperienceHealthAspect' | 'PregnancyHealthAspect' | 'PreventionHealthAspect' | 'PrognosisHealthAspect' | 'RelatedTopicsHealthAspect' | 'RisksOrComplicationsHealthAspect' | 'ScreeningHealthAspect' | 'SeeDoctorHealthAspect' | 'SelfCareHealthAspect' | 'SideEffectsHealthAspect' | 'StagesHealthAspect' | 'SymptomsHealthAspect' | 'TreatmentsHealthAspect' | 'TypesHealthAspect' | 'UsageOrScheduleHealthAspect';
}

export interface MedicalCondition {
  '@type': 'MedicalCondition';
  name: string;
  description?: string;
  code?: MedicalCode[];
  associatedAnatomy?: AnatomicalStructure[];
  possibleTreatment?: MedicalTherapy[];
  riskFactor?: MedicalRiskFactor[];
  expectedPrognosis?: string;
  epidemiology?: string;
}

export interface AnatomicalStructure {
  '@type': 'AnatomicalStructure';
  name: string;
  description?: string;
  associatedPathophysiology?: string;
  bodyLocation?: string;
}

export interface MedicalTherapy {
  '@type': 'MedicalTherapy' | 'DrugTherapy' | 'RadiationTherapy';
  name: string;
  description?: string;
  indication?: MedicalCondition;
  contraindication?: MedicalCondition[];
  seriousAdverseOutcome?: MedicalEntity[];
}

export interface MedicalRiskFactor {
  '@type': 'MedicalRiskFactor';
  name: string;
  description?: string;
  increasesRiskOf?: MedicalCondition[];
}

export interface MedicalEntity {
  '@type': 'MedicalEntity';
  name: string;
  description?: string;
  code?: MedicalCode[];
}

export interface Person {
  '@type': 'Person';
  name: string;
  url?: string;
  sameAs?: string[];
}

// JSON-LD Schema Generators
export class JSONLDGenerator {
  private static getBrandConfig(hostname?: string): BrandConfig {
    return getBrand(hostname);
  }

  private static getBaseUrl(hostname?: string): string {
    const brand = this.getBrandConfig(hostname);
    return brand.primaryDomain;
  }

  private static getOrganization(hostname?: string): Organization {
    const brand = this.getBrandConfig(hostname);
    return {
      '@type': 'Organization',
      name: brand.name,
      url: brand.primaryDomain,
      description: brand.description,
      logo: `${brand.primaryDomain}/logo.png`
    };
  }

  static generateWebSiteSchema(hostname?: string): WebSite {
    const brand = this.getBrandConfig(hostname);
    const baseUrl = this.getBaseUrl(hostname);
    return {
      '@type': 'WebSite',
      name: brand.name,
      description: brand.description,
      url: baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${baseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    };
  }

  static generateHospitalSchema(hospitalData: {
    id: string;
    name: string;
    city: string;
    state: string;
    address?: string;
    phone?: string;
    website?: string;
    specialties?: string[];
    priceRange?: string;
    services?: Array<{
      name: string;
      type: string;
      description?: string;
      averagePrice?: number;
    }>;
  }, hostname?: string): Hospital {
    const baseUrl = this.getBaseUrl(hostname);
    const organization = this.getOrganization(hostname);

    const hospital: Hospital = {
      '@type': 'Hospital',
      name: hospitalData.name,
      url: `${baseUrl}/hospital/${hospitalData.id}`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: hospitalData.city,
        addressRegion: hospitalData.state,
        addressCountry: 'US'
      }
    };

    if (hospitalData.address) {
      hospital.address!.streetAddress = hospitalData.address;
    }

    if (hospitalData.phone) {
      hospital.contactPoint = [{
        '@type': 'ContactPoint',
        telephone: hospitalData.phone,
        contactType: 'customer service'
      }];
    }

    if (hospitalData.specialties?.length) {
      hospital.medicalSpecialty = hospitalData.specialties;
    }

    if (hospitalData.priceRange) {
      hospital.priceRange = hospitalData.priceRange;
    }

    if (hospitalData.services?.length) {
      hospital.availableService = hospitalData.services.map(service => ({
        '@type': 'MedicalService',
        name: service.name,
        serviceType: service.type,
        description: service.description,
        provider: organization,
        ...(service.averagePrice && {
          offers: [{
            '@type': 'Offer',
            price: service.averagePrice.toString(),
            priceCurrency: 'USD',
            seller: hospital as Organization & { '@type': 'Organization' }
          }]
        })
      }));
    }

    return hospital;
  }

  static generateMedicalProcedureSchema(procedureData: {
    code: string;
    codingSystem: 'CPT' | 'DRG' | 'ICD-10' | 'HCPCS' | 'Revenue' | 'NDC';
    name: string;
    description?: string;
    alternateNames?: string[];
    bodyLocation?: string;
    procedureType?: string;
    priceRange?: string;
    averagePrice?: number;
    priceVariation?: {
      min: number;
      max: number;
      median: number;
    };
    medicalSpecialty?: string[];
    preparation?: string;
    followup?: string;
    howPerformed?: string;
    typicalAgeRange?: string;
    healthAspects?: string[];
  }, hostname?: string): MedicalProcedure {
    // Determine the most specific @type based on procedure characteristics
    let procedureType: 'MedicalProcedure' | 'SurgicalProcedure' | 'DiagnosticProcedure' = 'MedicalProcedure';
    
    if (procedureData.procedureType?.toLowerCase().includes('surgical') || 
        procedureData.name.toLowerCase().includes('surgery') ||
        procedureData.name.toLowerCase().includes('surgical')) {
      procedureType = 'SurgicalProcedure';
    } else if (procedureData.procedureType?.toLowerCase().includes('diagnostic') ||
               procedureData.name.toLowerCase().includes('scan') ||
               procedureData.name.toLowerCase().includes('test') ||
               procedureData.name.toLowerCase().includes('exam')) {
      procedureType = 'DiagnosticProcedure';
    }

    const procedure: MedicalProcedure = {
      '@type': procedureType,
      name: procedureData.name,
      code: [{
        '@type': 'MedicalCode',
        code: procedureData.code,
        codingSystem: procedureData.codingSystem,
        description: procedureData.description,
        version: '2024',
        url: `https://www.cms.gov/codes/${procedureData.codingSystem.toLowerCase()}/${procedureData.code}`
      }]
    };

    if (procedureData.description) {
      procedure.description = procedureData.description;
    }

    if (procedureData.alternateNames?.length) {
      procedure.alternateName = procedureData.alternateNames;
    }

    if (procedureData.bodyLocation) {
      procedure.bodyLocation = procedureData.bodyLocation;
    }

    if (procedureData.procedureType) {
      procedure.procedureType = procedureData.procedureType;
    }

    if (procedureData.medicalSpecialty?.length) {
      procedure.medicalSpecialty = procedureData.medicalSpecialty;
    }

    if (procedureData.preparation) {
      procedure.preparation = procedureData.preparation;
    }

    if (procedureData.followup) {
      procedure.followup = procedureData.followup;
    }

    if (procedureData.howPerformed) {
      procedure.howPerformed = procedureData.howPerformed;
    }

    if (procedureData.typicalAgeRange) {
      procedure.typicalAgeRange = procedureData.typicalAgeRange;
    }

    // Enhanced pricing with PriceSpecification
    if (procedureData.averagePrice) {
      procedure.price = {
        '@type': 'PriceSpecification',
        price: procedureData.averagePrice,
        priceCurrency: 'USD',
        priceType: 'ListPrice',
        validFrom: new Date().toISOString().split('T')[0],
        validThrough: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      };
    }

    // Enhanced offers with detailed pricing
    if (procedureData.priceVariation) {
      procedure.offers = [{
        '@type': 'Offer',
        price: procedureData.priceVariation.median.toString(),
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: procedureData.priceVariation.median,
          priceCurrency: 'USD',
          priceType: 'ListPrice',
          validFrom: new Date().toISOString().split('T')[0]
        },
        eligibleQuantity: {
          '@type': 'QuantitativeValue',
          value: 1,
          unitText: 'procedure'
        }
      }];

      procedure.priceRange = `$${procedureData.priceVariation.min}-$${procedureData.priceVariation.max}`;
    } else if (procedureData.priceRange) {
      procedure.priceRange = procedureData.priceRange;
    }

    return procedure;
  }

  static generateDatasetSchema(datasetData: {
    name: string;
    description: string;
    url: string;
    datePublished?: string;
    dateModified?: string;
    keywords?: string[];
    downloadFormats?: Array<{
      format: string;
      url: string;
      size?: string;
    }>;
  }, hostname?: string): Dataset {
    const dataset: Dataset = {
      '@type': 'Dataset',
      name: datasetData.name,
      description: datasetData.description,
      url: datasetData.url,
      creator: this.getOrganization(hostname)
    };

    if (datasetData.datePublished) {
      dataset.datePublished = datasetData.datePublished;
    }

    if (datasetData.dateModified) {
      dataset.dateModified = datasetData.dateModified;
    }

    if (datasetData.keywords?.length) {
      dataset.keywords = datasetData.keywords;
    }

    if (datasetData.downloadFormats?.length) {
      dataset.distribution = datasetData.downloadFormats.map(format => ({
        '@type': 'DataDownload',
        encodingFormat: format.format,
        contentUrl: format.url,
        ...(format.size && { contentSize: format.size })
      }));
    }

    return dataset;
  }

  static generateHospitalRankingSchema(rankingData: {
    title: string;
    description: string;
    datePublished: string;
    rankings: Array<{
      hospitalId: string;
      hospitalName: string;
      rank: number;
      score?: number;
      category: string;
    }>;
  }, hostname?: string) {
    // Create a structured data representation for hospital rankings
    const rankings = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: rankingData.title,
      description: rankingData.description,
      datePublished: rankingData.datePublished,
      numberOfItems: rankingData.rankings.length,
      itemListElement: rankingData.rankings.map((ranking, index) => {
        const baseUrl = this.getBaseUrl(hostname);
        return {
          '@type': 'ListItem',
          position: ranking.rank,
          name: ranking.hospitalName,
          url: `${baseUrl}/hospital/${ranking.hospitalId}`,
          item: {
            '@type': 'Hospital',
            name: ranking.hospitalName,
            url: `${baseUrl}/hospital/${ranking.hospitalId}`,
            ...(ranking.score && {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: ranking.score,
                bestRating: 100,
                worstRating: 0,
                description: `${ranking.category} Score`
              }
            })
          }
        };
      })
    };

    return rankings;
  }

  static generateBreadcrumbSchema(breadcrumbs: Array<{
    name: string;
    url: string;
  }>) {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url
      }))
    };
  }

  static generatePriceComparisonSchema(comparisonData: {
    procedureName: string;
    procedureCode: string;
    hospitals: Array<{
      hospitalId: string;
      hospitalName: string;
      price: number;
      city: string;
      state: string;
    }>;
  }, hostname?: string) {
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${comparisonData.procedureName} Price Comparison`,
      description: `Hospital price comparison for ${comparisonData.procedureName} (${comparisonData.procedureCode})`,
      numberOfItems: comparisonData.hospitals.length,
      itemListElement: comparisonData.hospitals.map((hospital, index) => {
        const baseUrl = this.getBaseUrl(hostname);
        return {
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Offer',
            name: `${comparisonData.procedureName} at ${hospital.hospitalName}`,
            price: hospital.price.toString(),
            priceCurrency: 'USD',
            seller: {
              '@type': 'Hospital',
              name: hospital.hospitalName,
              url: `${baseUrl}/hospital/${hospital.hospitalId}`,
              address: {
                '@type': 'PostalAddress',
                addressLocality: hospital.city,
                addressRegion: hospital.state,
                addressCountry: 'US'
              }
            },
            itemOffered: {
              '@type': 'MedicalProcedure',
              name: comparisonData.procedureName,
              code: {
                '@type': 'MedicalCode',
                code: comparisonData.procedureCode
              }
            }
          }
        };
      })
    };
  }

  static generateConnectToCareSchema(data: {
    scenarioName: string;
    scenarioUrl: string;
    organizations: Array<{
      name: string;
      description: string;
      url: string;
      phone?: string;
      mission?: string;
    }>;
  }) {
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Connect to Care — ${data.scenarioName}`,
      url: `${data.scenarioUrl}#connect-to-care`,
      numberOfItems: data.organizations.length,
      itemListElement: data.organizations.map((org, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'NGO',
          name: org.name,
          description: org.mission || org.description,
          url: org.url,
          ...(org.phone && { telephone: org.phone }),
        },
      })),
    };
  }

  static wrapWithContext(schema: any): any {
    return {
      '@context': 'https://schema.org',
      ...schema
    };
  }

  static generateMultipleSchemas(schemas: any[]): any[] {
    return schemas.map(schema => this.wrapWithContext(schema));
  }

  // Helper function to embed JSON-LD in HTML head
  static embedJSONLD(schema: any): string {
    return `<script type="application/ld+json">${JSON.stringify(schema, null, 2)}</script>`;
  }

  // Generate comprehensive SEO data for a page
  static generatePageSchema(pageType: string, data: any, hostname?: string): any[] {
    const schemas: any[] = [];
    const baseUrl = this.getBaseUrl(hostname);

    // Always include website schema for homepage
    if (pageType === 'home') {
      schemas.push(this.generateWebSiteSchema(hostname));
    }

    switch (pageType) {
      case 'hospital_profile':
        schemas.push(this.generateHospitalSchema(data, hostname));
        if (data.breadcrumbs) {
          schemas.push(this.generateBreadcrumbSchema(data.breadcrumbs));
        }
        break;

      case 'cpt_code_detail':
      case 'drg_code_detail':
      case 'revenue_code_detail':
      case 'drug_code_detail':
        schemas.push(this.generateMedicalProcedureSchema(data, hostname));
        if (data.priceComparison) {
          schemas.push(this.generatePriceComparisonSchema(data.priceComparison, hostname));
        }
        if (data.breadcrumbs) {
          schemas.push(this.generateBreadcrumbSchema(data.breadcrumbs));
        }
        break;

      case 'hospital_rankings':
        schemas.push(this.generateHospitalRankingSchema(data, hostname));
        schemas.push(this.generateDatasetSchema({
          name: 'Hospital Price Rankings Dataset',
          description: 'Comprehensive hospital pricing data and rankings',
          url: `${baseUrl}/hospital-rankings`,
          dateModified: new Date().toISOString().split('T')[0],
          keywords: ['hospital rankings', 'healthcare costs', 'price transparency']
        }, hostname));
        break;

      default:
        // For other page types, include basic organization schema
        schemas.push(this.getOrganization(hostname));
    }

    return this.generateMultipleSchemas(schemas);
  }
}

// Hook for React components to easily add JSON-LD
export const useJSONLD = (pageType: string, data: any) => {
  // Detect hostname on client side if available
  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const schemas = JSONLDGenerator.generatePageSchema(pageType, data, hostname);

  // Return the schemas for use in React Helmet or similar
  return schemas.map(schema => ({
    type: 'application/ld+json',
    content: JSON.stringify(schema, null, 2)
  }));
};

// Export common schema patterns for reuse
export const getCommonSchemas = (hostname?: string) => {
  return {
    WEBSITE: JSONLDGenerator.generateWebSiteSchema(hostname),
    ORGANIZATION: JSONLDGenerator.getOrganization(hostname)
  };
};

// Legacy export for backwards compatibility
export const COMMON_SCHEMAS = getCommonSchemas();